/**
 * Feito pelo agente netexperts
 * Módulo: AnalyticsObra — painel de execuções de busca de dados da obra (admin)
 * Data: 2026-06-22
 *
 * Lista as configurações/execuções agendadas de sincronização de dados da obra com o ERP.
 * Busca paginada via backend (GET /configuracao-obra-busca/:obra_id). Nunca acumula páginas
 * no estado: guarda apenas a página visível.
 */
import { useEffect, useState } from 'react'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import FormBusca from '../../../../Components/Forms/Busca'
import { useConfiguracaoObraBusca } from '../hooks/useObraAnalytics'
import { BUSCA_CONFIG } from '../config/areaAnalytics'

const config = BUSCA_CONFIG

const ConfiguracaoBuscaTab = ({ obraId }) => {
    const { rows, loading, error, fetchExecucoes } = useConfiguracaoObraBusca(obraId)
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedFilterField, setSelectedFilterField] = useState(config.filterOptions[0].value)
    const [selectedSortField, setSelectedSortField] = useState(config.sortOptions[0].value)
    const [sortDirection, setSortDirection] = useState('desc')
    const [recordsPerPage, setRecordsPerPage] = useState(10)
    const [page, setPage] = useState(0)

    const load = (overrides = {}) => {
        const perPage = overrides.recordsPerPage ?? recordsPerPage
        fetchExecucoes({
            page: overrides.page ?? 0,
            per_page: perPage,
            sort: overrides.selectedSortField ?? selectedSortField,
            sort_dir: overrides.sortDirection ?? sortDirection,
            filter: overrides.selectedFilterField ?? selectedFilterField,
            search: overrides.searchTerm !== undefined ? overrides.searchTerm : searchTerm,
        })
    }

    useEffect(() => {
        setPage(0)
        load({ page: 0 })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [obraId])

    const search = (formData) => {
        setPage(0)
        load({ ...formData, page: 0 })
    }

    const handleClear = () => {
        setSearchTerm('')
        setSelectedFilterField(config.filterOptions[0].value)
        setSelectedSortField(config.sortOptions[0].value)
        setSortDirection('desc')
        setRecordsPerPage(10)
        setPage(0)
        load({ page: 0, recordsPerPage: 10, selectedSortField: config.sortOptions[0].value, sortDirection: 'desc', selectedFilterField: config.filterOptions[0].value, searchTerm: '' })
    }

    const goTo = (newPage) => {
        setPage(newPage)
        load({ page: newPage })
    }

    const Icon = config.icon
    // O backend não retorna total: habilita "próximo" enquanto a página vier cheia.
    const hasNext = rows.length === recordsPerPage

    return (
        <div className="space-y-4">
            <FormBusca
                filterOptions={config.filterOptions}
                sortOptions={config.sortOptions}
                search={search}
                handleClear={handleClear}
                color="yellow"
                haveData={rows.length > 0}
                items={{ searchTerm, setSearchTerm, selectedFilterField, setSelectedFilterField, selectedSortField, setSelectedSortField, sortDirection, setSortDirection, recordsPerPage, setRecordsPerPage }}
            />

            <div data-netexperts="true" className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-gray-900 rounded-lg">
                            <Icon className="w-4 h-4 text-brand-yellow" />
                        </div>
                        <h2 className="text-sm font-semibold text-gray-900">{config.title}</h2>
                    </div>
                    <span className="text-xs text-gray-400">{rows.length} registros nesta página</span>
                </div>

                <div className="overflow-x-auto">
                    <div className="min-w-[920px]">
                        <div className="grid gap-2 px-4 py-2 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide" style={{ gridTemplateColumns: `repeat(${config.columns.length}, minmax(0, 1fr))` }}>
                            {config.columns.map((col) => (
                                <div key={col.key} className="text-center">{col.label}</div>
                            ))}
                        </div>
                        <div className="divide-y divide-gray-50">
                            {loading ? (
                                <div className="px-4 py-10 text-center text-sm text-gray-400">Carregando…</div>
                            ) : error ? (
                                <div className="px-4 py-10 text-center text-sm text-rose-500">Erro ao carregar as execuções de busca.</div>
                            ) : rows.length === 0 ? (
                                <div className="px-4 py-10 text-center text-sm text-gray-400">Nenhuma execução de busca encontrada.</div>
                            ) : (
                                rows.map((row, idx) => (
                                    <div key={row.id ?? idx} className="grid gap-2 px-4 py-3 hover:bg-gray-50 transition-colors" style={{ gridTemplateColumns: `repeat(${config.columns.length}, minmax(0, 1fr))` }}>
                                        {config.columns.map((col) => {
                                            const display = col.render
                                                ? col.render(row)
                                                : col.format
                                                    ? col.format(row[col.key])
                                                    : (row[col.key] ?? '-')
                                            return (
                                                <div key={col.key} className="text-center text-xs text-gray-600 truncate" title={String(display ?? '-')}>
                                                    {display}
                                                </div>
                                            )
                                        })}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {(rows.length > 0 || page > 0) && (
                <div className="flex items-center justify-between bg-white border border-gray-100 rounded-2xl px-5 py-3 shadow-sm">
                    <span className="text-sm text-gray-500">Página {page + 1}</span>
                    <div className="flex gap-2">
                        <button onClick={() => goTo(page - 1)} disabled={page === 0} className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                            <ChevronLeftIcon className="w-4 h-4" /> Anterior
                        </button>
                        <button onClick={() => goTo(page + 1)} disabled={!hasNext} className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                            Próximo <ChevronRightIcon className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default ConfiguracaoBuscaTab
